import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import BackMethods from "../../back_component/BackMethods";

function ModalHeaderPro({ title, onHide }) {
  return (
    <div className="users-admin-modal__header">
      <div className="users-admin-modal__header-main">
        <h5 className="users-admin-modal__title mb-0">{title}</h5>
      </div>
      <button type="button" className="btn-close" onClick={onHide} aria-label="إغلاق" />
    </div>
  );
}

function InlineField({ label, htmlFor, children }) {
  return (
    <div className="users-admin-modal__inline-field">
      <Form.Label htmlFor={htmlFor} className="users-admin-modal__inline-label">
        {label}
      </Form.Label>
      <div className="users-admin-modal__inline-control">{children}</div>
    </div>
  );
}

function CreatePermissionModal({ show, onHide, onSaved }) {
  const { backEnd_request } = BackMethods();
  const [permission, setPermission] = useState({
    name: "",
    displayName: "",
    description: "",
    category: "",
  });
  const [categories, setCategories] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!show) return;
    setPermission({
      name: "",
      displayName: "",
      description: "",
      category: "",
    });
    setFormError("");
    setIsSubmitting(false);

    let cancelled = false;
    (async () => {
      try {
        const res = await backEnd_request.get("/admin/Permissions/categories");
        if (!cancelled) setCategories(res.data || []);
      } catch {
        if (!cancelled) setCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, backEnd_request]);

  const handleChange = (field, value) => {
    setPermission((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!permission.name.trim()) return "الرجاء إدخال الاسم";
    if (!permission.displayName.trim()) return "الرجاء إدخال الاسم الظاهر";
    if (!permission.category.trim()) return "الرجاء اختيار الفئة";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setIsSubmitting(true);
    try {
      await backEnd_request.post("/admin/Permissions", permission);
      onSaved?.({ message: "تمت إضافة الصلاحية بنجاح.", variant: "success" });
      onHide();
    } catch (err) {
      setFormError(err.response?.data?.message || "حدث خطأ أثناء الإرسال");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="إضافة صلاحية" onHide={onHide} />
      <Modal.Body>
        <p className="users-admin-modal__hint">
          <i className="bi bi-plus-circle" />
          <span>حافظ على الاسم التقني واضحاً وسهل الاستخدام داخل النظام.</span>
        </p>
        <Form onSubmit={handleSubmit}>
          <Row className="g-2 gx-lg-3">
            <Col md={6}>
              <InlineField label="الاسم (Name)" htmlFor="perm-create-name">
                <Form.Control
                  size="sm"
                  className="rounded-2"
                  id="perm-create-name"
                  value={permission.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </InlineField>
            </Col>
            <Col md={6}>
              <InlineField label="الاسم الظاهر" htmlFor="perm-create-displayName">
                <Form.Control
                  size="sm"
                  className="rounded-2"
                  id="perm-create-displayName"
                  value={permission.displayName}
                  onChange={(e) => handleChange("displayName", e.target.value)}
                />
              </InlineField>
            </Col>
            <Col xs={12}>
              <InlineField label="الفئة" htmlFor="perm-create-category">
                <Form.Select
                  size="sm"
                  className="rounded-2"
                  id="perm-create-category"
                  value={permission.category}
                  onChange={(e) => handleChange("category", e.target.value)}
                >
                  <option value="">اختر الفئة</option>
                  {categories.map((cat, index) => (
                    <option key={index} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Form.Select>
              </InlineField>
            </Col>
            <Col xs={12}>
              <InlineField label="الوصف" htmlFor="perm-create-description">
                <Form.Control
                  as="textarea"
                  rows={3}
                  size="sm"
                  className="rounded-2"
                  id="perm-create-description"
                  value={permission.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                />
              </InlineField>
            </Col>
          </Row>
          {formError ? (
            <div className="alert alert-danger py-1 px-2 small mb-0 mt-2" role="alert">
              {formError}
            </div>
          ) : null}
          <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>
              إلغاء
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="ms-1" /> إضافة
                </>
              ) : (
                "إضافة الصلاحية"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

function EditPermissionModal({ show, permissionId, onHide, onSaved }) {
  const { backEnd_request } = BackMethods();
  const [permission, setPermission] = useState({
    name: "",
    displayName: "",
    description: "",
    category: "",
    isSystemPermission: false,
  });
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!show || !permissionId) return;
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        setFormError("");
        const [permRes, catRes] = await Promise.all([
          backEnd_request.get(`/admin/Permissions/${permissionId}`),
          backEnd_request.get("/admin/Permissions/categories"),
        ]);
        if (cancelled) return;
        const p = permRes.data || permRes || {};
        setPermission({
          name: p.name || "",
          displayName: p.displayName || "",
          description: p.description || "",
          category: p.category || "",
          isSystemPermission: Boolean(p.isSystemPermission),
        });
        setCategories(catRes.data || []);
      } catch (err) {
        if (!cancelled) {
          setFormError(err.response?.data?.message || "تعذر تحميل بيانات الصلاحية");
          setCategories([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, permissionId, backEnd_request]);

  const validate = () => {
    if (!permission.displayName.trim()) return "الرجاء إدخال الاسم الظاهر";
    if (!permission.category.trim()) return "الرجاء اختيار الفئة";
    if (!permission.isSystemPermission && !permission.name.trim()) return "الرجاء إدخال الاسم التقني";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        name: permission.name,
        displayName: permission.displayName,
        description: permission.description,
        category: permission.category,
      };
      await backEnd_request.post(`/admin/Permissions/${permissionId}`, payload);
      onSaved?.({ message: "تم تعديل الصلاحية بنجاح.", variant: "success" });
      onHide();
    } catch (err) {
      setFormError(err.response?.data?.message || "تعذر حفظ التعديلات");
    } finally {
      setIsSubmitting(false);
    }
  };

  const chip = useMemo(() => permission.displayName || permission.name, [permission.displayName, permission.name]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="تعديل صلاحية" onHide={onHide} />
      <Modal.Body>
        {chip ? (
          <div className="users-admin-modal__user-chip mb-2 py-1">
            <i className="bi bi-shield-check text-secondary small" />
            <span className="text-muted" style={{ fontSize: "0.75rem" }}>
              الصلاحية
            </span>
            <strong className="small">{chip}</strong>
          </div>
        ) : null}

        <Form onSubmit={handleSubmit}>
          {isLoading ? (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
            </div>
          ) : null}
          <Row className="g-2 gx-lg-3">
            <Col md={6}>
              <InlineField label="الاسم (Name)" htmlFor="perm-edit-name">
                <Form.Control
                  size="sm"
                  className="rounded-2"
                  id="perm-edit-name"
                  value={permission.name}
                  onChange={(e) => setPermission((prev) => ({ ...prev, name: e.target.value }))}
                  disabled={isLoading || permission.isSystemPermission}
                />
              </InlineField>
            </Col>
            <Col md={6}>
              <InlineField label="الاسم الظاهر" htmlFor="perm-edit-displayName">
                <Form.Control
                  size="sm"
                  className="rounded-2"
                  id="perm-edit-displayName"
                  value={permission.displayName}
                  onChange={(e) => setPermission((prev) => ({ ...prev, displayName: e.target.value }))}
                  disabled={isLoading}
                />
              </InlineField>
            </Col>
            <Col xs={12}>
              <InlineField label="الفئة" htmlFor="perm-edit-category">
                <Form.Select
                  size="sm"
                  className="rounded-2"
                  id="perm-edit-category"
                  value={permission.category}
                  onChange={(e) => setPermission((prev) => ({ ...prev, category: e.target.value }))}
                  disabled={isLoading || permission.isSystemPermission}
                >
                  <option value="">اختر الفئة</option>
                  {categories.map((cat, index) => (
                    <option key={index} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Form.Select>
              </InlineField>
            </Col>
            <Col xs={12}>
              <InlineField label="الوصف" htmlFor="perm-edit-description">
                <Form.Control
                  as="textarea"
                  rows={3}
                  size="sm"
                  className="rounded-2"
                  id="perm-edit-description"
                  value={permission.description}
                  onChange={(e) => setPermission((prev) => ({ ...prev, description: e.target.value }))}
                  disabled={isLoading}
                />
              </InlineField>
            </Col>
          </Row>
          {permission.isSystemPermission ? (
            <p className="text-muted small mb-0 mt-2">
              <i className="bi bi-info-circle ms-1" />
              صلاحية نظام: الاسم التقني والفئة مقفولان للحفاظ على اتساق النظام.
            </p>
          ) : null}
          {formError ? (
            <div className="alert alert-danger py-1 px-2 small mb-0 mt-2" role="alert">
              {formError}
            </div>
          ) : null}
          <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>
              إلغاء
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="ms-1" /> حفظ
                </>
              ) : (
                "حفظ التعديلات"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

function PermissionDetailsModal({ show, permissionId, onHide }) {
  const { backEnd_request } = BackMethods();
  const [perm, setPerm] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!show || !permissionId) return;
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const res = await backEnd_request.get(`/admin/Permissions/${permissionId}`);
        if (cancelled) return;
        setPerm(res.data || res || null);
      } catch {
        if (!cancelled) setPerm(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, permissionId, backEnd_request]);

  const descriptionValue = useMemo(() => {
    if (!perm) return "";
    const v =
      perm.description ??
      perm.Description ??
      perm.desc ??
      "";
    return typeof v === "string" ? v : "";
  }, [perm]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="معلومات الصلاحية" onHide={onHide} />
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-2">
            <Spinner animation="border" size="sm" />
          </div>
        ) : perm ? (
          <>
            <p className="users-admin-modal__hint" style={{ marginBottom: "0.6rem" }}>
              <i className="bi bi-info-circle" />
              <span>
                {perm.displayName || "-"} ({perm.name || "-"})
              </span>
            </p>
            <Row className="g-2 gx-lg-3">
              <Col lg={6}>
                <InlineField label="المعرّف" htmlFor="perm-details-id">
                  <div className="roles-admin-modal__readonly-value">{perm.permissionId ?? "-"}</div>
                </InlineField>
                <InlineField label="الاسم التقني" htmlFor="perm-details-name">
                  <div className="roles-admin-modal__readonly-value">{perm.name || "-"}</div>
                </InlineField>
                <InlineField label="الاسم الظاهر" htmlFor="perm-details-displayName">
                  <div className="roles-admin-modal__readonly-value">{perm.displayName || "-"}</div>
                </InlineField>
              </Col>
              <Col lg={6}>
                <InlineField label="الفئة" htmlFor="perm-details-category">
                  <div className="roles-admin-modal__readonly-value">{perm.category || "-"}</div>
                </InlineField>
                <InlineField label="النوع" htmlFor="perm-details-type">
                  <div className="roles-admin-modal__readonly-value">
                    {perm.isSystemPermission ? "صلاحية نظام" : "صلاحية عادية"}
                  </div>
                </InlineField>
                <InlineField label="الوصف" htmlFor="perm-details-description">
                  <div className="roles-admin-modal__readonly-value roles-admin-modal__readonly-value--desc">
                    {descriptionValue.trim() || "-"}
                  </div>
                </InlineField>
              </Col>
            </Row>
            <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
              <Button variant="primary" size="sm" type="button" onClick={onHide}>
                تم
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center text-muted py-2 small">تعذر تحميل معلومات الصلاحية</div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default function PermissionsManagementModals({
  modal,
  permissionId,
  onClose,
  onSaved,
  canCreate = true,
}) {
  return (
    <>
      <CreatePermissionModal show={canCreate && modal === "create"} onHide={onClose} onSaved={onSaved} />
      <EditPermissionModal show={modal === "edit"} permissionId={permissionId} onHide={onClose} onSaved={onSaved} />
      <PermissionDetailsModal show={modal === "details"} permissionId={permissionId} onHide={onClose} />
    </>
  );
}
